// ADV-264 — Figma "KONTO | Layer | Widerruf · Kauf / Finanzierung" (desktop dialog, mobile
// bottom sheet) on the shared md3 Modal. The customer checks the contract data, gives the
// e-mail for the confirmation of receipt and confirms. Purchase and financing share the layout;
// only the note differs: the seller for a purchase, the lender's own withdrawal for a financing.
import * as React from 'react';
import { Modal } from '@bytebrand/fe-ui-core-autobahn/ui';
import UserActionsContactAPI from '../../../services/UserActionsContactAPI';
import { buyingTypeOf, carSubtitle, carTitle, companyName, lenderOf } from './orderData';
import closeIcon from './icons/dialog-close.svg';
import errorIcon from './icons/field-error.svg';
const ns = (key) => `MyOrderPage:orders.wd.${key}`;
// company names go into the text as they are (React escapes them; i18next would turn & into &amp;)
const raw = { escapeValue: false };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * Sends the Widerruf (user-actions-contact contracts/withdrawal): the order's Odoo request gets the
 * tag "Widerruf" and a note with it. Rejects when it was not sent, so
 * the dialog never claims otherwise.
 */
export const sendWithdrawal = (request) => UserActionsContactAPI.sendWithdrawal(request).then(() => undefined);
const DataItem = ({ label, children }) => (React.createElement("div", { className: "ao-wdd-item" },
    React.createElement("dt", { className: "ao-wdd-label" }, label),
    children));
const WithdrawalModal = ({ lead, seller, email: initialEmail, t, onClose, onSent, onError }) => {
    const [email, setEmail] = React.useState(initialEmail);
    const [invalid, setInvalid] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const busy = React.useRef(false);
    const inputRef = React.useRef(null);
    const inputId = React.useId();
    const { car } = lead;
    const type = buyingTypeOf(lead);
    const name = [lead.customerAddress && lead.customerAddress.firstname, lead.customerAddress && lead.customerAddress.lastname]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ');
    const vehicle = [carTitle(car), carSubtitle(car)].filter(Boolean).join(' ');
    let note = '';
    if (type === 'financing') {
        note = t(ns('noteFinancing'), { lender: companyName(lenderOf(car, 'financing', t)), interpolation: raw });
    }
    else {
        note = [seller ? t(ns('noteBuyPartner'), { name: seller.name, interpolation: raw }) : '', t(ns('noteBuy'))].filter(Boolean).join(' ');
    }
    const confirm = async () => {
        if (busy.current)
            return;
        const value = email.trim();
        if (!EMAIL.test(value)) {
            setInvalid(true);
            if (inputRef.current)
                inputRef.current.focus();
            return;
        }
        busy.current = true;
        setSending(true);
        try {
            await sendWithdrawal({ carId: car._id, request: lead.request, buyingType: type, email: value });
            onSent();
        }
        catch {
            onError();
        }
        finally {
            busy.current = false;
            setSending(false);
        }
    };
    const fieldClass = ['ao-wdd-field', invalid ? 'ao-wdd-field--error' : '', email ? '' : 'ao-wdd-field--empty'].filter(Boolean).join(' ');
    return (React.createElement(Modal, { open: true, onClose: onClose, className: "ao-wdd", closeIcon: React.createElement("img", { src: closeIcon, alt: "", width: 22, height: 22 }), title: React.createElement("span", { className: "ao-dlg-title" }, t(ns('title'))), footer: (React.createElement(React.Fragment, null,
            React.createElement("button", { type: "button", className: "ao-wdd-cancel", onClick: onClose }, t(ns('cancel'))),
            React.createElement("button", { type: "button", className: sending ? 'ao-wdd-confirm ao-wdd-confirm--loading' : 'ao-wdd-confirm', onClick: confirm, "aria-busy": sending },
                React.createElement("span", { className: "ao-wdd-confirm-label" }, t(ns('confirm'))),
                sending ? React.createElement("span", { className: "ao-wdd-spinner", "aria-hidden": "true" }) : null))) },
        React.createElement("div", { className: "ao-wdd-content" },
            React.createElement("p", { className: "ao-wdd-intro" }, t(ns('intro'))),
            React.createElement("dl", { className: "ao-wdd-data" },
                name ? React.createElement(DataItem, { label: t(ns('name')) },
                    React.createElement("dd", { className: "ao-wdd-value" }, name)) : null,
                React.createElement(DataItem, { label: t(ns('contract')) },
                    React.createElement("dd", { className: "ao-wdd-value" }, t(ns('contractValue'), { car: vehicle, interpolation: raw }))),
                lead.request ? React.createElement(DataItem, { label: t(ns('number')) },
                    React.createElement("dd", { className: "ao-wdd-value" }, lead.request)) : null,
                seller ? (React.createElement(DataItem, { label: t(ns('partner')) },
                    React.createElement("dd", { className: "ao-wdd-value ao-wdd-value--strong" }, seller.name),
                    seller.address ? React.createElement("dd", { className: "ao-wdd-address" }, seller.address) : null)) : null),
            React.createElement("div", { className: fieldClass },
                React.createElement("label", { className: "ao-wdd-field-label", htmlFor: inputId }, t(ns('email'))),
                React.createElement("input", { ref: inputRef, id: inputId, className: "ao-wdd-field-input", name: "email", type: "email", autoComplete: "email", spellCheck: false, value: email, "aria-invalid": invalid, onChange: (event) => {
                        setEmail(event.target.value);
                        if (invalid)
                            setInvalid(false);
                    }, onKeyDown: (event) => {
                        if (event.key === 'Enter')
                            confirm();
                    } }),
                invalid ? React.createElement("img", { className: "ao-wdd-field-ic", src: errorIcon, alt: "", width: 16, height: 16 }) : null),
            React.createElement("div", { className: "ao-wdd-note" },
                React.createElement("p", { className: "ao-wdd-note-text" }, note)))));
};
export default WithdrawalModal;
